import { z } from 'zod';
export const ASSISTANT_MAX_MESSAGES = 24;
export const ASSISTANT_MAX_MESSAGE_CHARS = 48_000;
export const ASSISTANT_MAX_REQUEST_BYTES = 100_000;
/**
 * Separate, generous cap for a trailing tool-approval message. Approval
 * responses replay the tool call's full input (a create_finding poster
 * document can approach the 2 MB poster limit), so holding them to the
 * ordinary request cap would reject the very confirmation turn that executes
 * the write. Everything before the approval message stays tightly capped.
 *
 * Fitting cannot shrink an approval message (the server needs the intact
 * input to execute the confirmed call), so this cap is a server-side
 * boundary, not a client-side target: every approval-gated tool bounds its
 * input schema well below it (poster html ≤ 2M chars), making an over-cap
 * approval unreachable rather than merely rejected.
 */
export const ASSISTANT_MAX_APPROVAL_BYTES = 2_500_000;
export const AssistantMessageSchema = z
    .object({
    id: z.string().min(1).max(128),
    role: z.enum(['user', 'assistant']),
    // AI SDK tool parts evolve with the SDK. The server validates their complete
    // shape against the bound agent before execution; this outer contract owns
    // transport/history bounds only.
    parts: z.array(z.unknown()).min(1).max(32),
})
    .passthrough();
/** What the user is looking at when they open the assistant, for grounding. */
export const AssistantPageContextSchema = z
    .object({
    research_id: z.string().min(1).max(128).optional(),
    digest_date: z.iso.date().optional(),
    item_id: z.string().min(1).max(128).optional(),
    doc_slug: z
        .string()
        .regex(/^[a-z0-9][a-z0-9-]*$/u)
        .max(200)
        .optional(),
    package_slug: z
        .string()
        .regex(/^[a-z0-9][a-z0-9-]*$/u)
        .max(200)
        .optional(),
    topic_slug: z
        .string()
        .regex(/^[a-z0-9][a-z0-9-]*$/u)
        .max(200)
        .optional(),
})
    .strict();
/** Human-selected save options; never part of the model's tool input. */
export const FindingApprovalSchema = z
    .object({
    tool_call_id: z.string().min(1).max(128),
    visibility: z.enum(['private', 'internal']),
    share_confirmed: z.boolean(),
    topic: z
        .string()
        .regex(/^[a-z0-9][a-z0-9-]*$/u)
        .max(64),
})
    .strict();
export const AssistantRequestSchema = z
    .object({
    id: z.string().min(1).max(128).optional(),
    trigger: z.enum(['submit-message', 'regenerate-message']).optional(),
    messageId: z.string().min(1).max(128).nullable().optional(),
    messages: z.array(AssistantMessageSchema).min(1).max(ASSISTANT_MAX_MESSAGES),
    page_context: AssistantPageContextSchema.optional(),
    finding_approvals: z.array(FindingApprovalSchema).max(16).optional(),
})
    .strict()
    .superRefine((request, context) => {
    const { body, approval } = splitTrailingApproval(request.messages);
    const characters = countConversationChars(request.messages);
    if (characters > ASSISTANT_MAX_MESSAGE_CHARS) {
        context.addIssue({
            code: 'custom',
            message: `conversation exceeds ${ASSISTANT_MAX_MESSAGE_CHARS} characters`,
        });
    }
    if (JSON.stringify(body).length > ASSISTANT_MAX_REQUEST_BYTES) {
        context.addIssue({
            code: 'custom',
            message: `request exceeds ${ASSISTANT_MAX_REQUEST_BYTES} bytes`,
        });
    }
    if (approval && JSON.stringify([approval]).length > ASSISTANT_MAX_APPROVAL_BYTES) {
        context.addIssue({
            code: 'custom',
            message: `approval message exceeds ${ASSISTANT_MAX_APPROVAL_BYTES} bytes`,
        });
    }
});
function isTextPart(part) {
    if (typeof part !== 'object' || part === null)
        return false;
    const candidate = part;
    return candidate.type === 'text' && typeof candidate.text === 'string';
}
function hasApprovalResponse(parts) {
    return parts.some((part) => {
        if (typeof part !== 'object' || part === null)
            return false;
        const candidate = part;
        return (candidate.state === 'approval-responded' && typeof candidate.approval?.approved === 'boolean');
    });
}
/**
 * Split a trailing tool-approval message (if any) from the ordinary history.
 * The approval message is byte-capped separately (ASSISTANT_MAX_APPROVAL_BYTES)
 * because it replays the tool call's full input; shared so client fitting and
 * server validation cannot disagree.
 */
function splitTrailingApproval(messages) {
    const last = messages.at(-1);
    if (last !== undefined && hasApprovalResponse(last.parts)) {
        return { body: messages.slice(0, -1), approval: last };
    }
    return { body: messages, approval: null };
}
/**
 * Total text characters across a conversation — the exact measurement the
 * request schema enforces, shared so client fitting and server validation
 * cannot disagree.
 */
export function countConversationChars(messages) {
    return messages.reduce((total, message) => {
        return (total +
            message.parts.reduce((subtotal, part) => {
                if (!isTextPart(part))
                    return subtotal;
                return subtotal + part.text.length;
            }, 0));
    }, 0);
}
/**
 * Keep ordinary conversation history text-only so completed tool calls are not
 * replayed. Preserve only the final approval response needed by AI SDK to run a
 * newly confirmed write. Shared by the server (defense before running the
 * agent) and the client (the wire payload never carries tool parts).
 */
export function sanitizeAssistantHistory(messages) {
    return messages
        .map((message, index) => {
        const isFinalApproval = index === messages.length - 1 && hasApprovalResponse(message.parts);
        if (isFinalApproval)
            return message;
        return { ...message, parts: message.parts.filter(isTextPart) };
    })
        .filter((message) => message.parts.length > 0);
}
export const ASSISTANT_TRIM_MARKER = '…[earlier content trimmed]';
/** Head-truncate a message's text parts so their total length fits `budget`. */
function truncateMessageText(message, budget) {
    let excess = countConversationChars([message]) + ASSISTANT_TRIM_MARKER.length - budget;
    let markerPending = true;
    const parts = message.parts.flatMap((part) => {
        if (!isTextPart(part) || excess <= 0)
            return [part];
        if (part.text.length <= excess) {
            excess -= part.text.length;
            return [];
        }
        const kept = part.text.slice(excess);
        excess = 0;
        markerPending = false;
        return [{ ...part, text: `${ASSISTANT_TRIM_MARKER}${kept}` }];
    });
    // Every text part was dropped whole — keep a marker so the message survives.
    if (markerPending)
        parts.unshift({ type: 'text', text: ASSISTANT_TRIM_MARKER });
    return { ...message, parts };
}
/**
 * Fit a conversation into the request caps before sending: sanitize to
 * text-only history, then drop oldest messages (always keeping the newest)
 * until the count, text-char, and serialized-byte caps all hold. Backstop: if
 * the newest message alone exceeds a cap, head-truncate its text with a
 * marker. Guarantees the result passes AssistantRequestSchema's size checks.
 */
export function fitAssistantHistory(messages) {
    // Pre-slice to the count cap so the drop loop is bounded regardless of how
    // long the displayed conversation grows.
    let fitted = sanitizeAssistantHistory(messages).slice(-ASSISTANT_MAX_MESSAGES);
    const overCaps = (candidate) => {
        const { body } = splitTrailingApproval(candidate);
        return (candidate.length > ASSISTANT_MAX_MESSAGES ||
            countConversationChars(candidate) > ASSISTANT_MAX_MESSAGE_CHARS ||
            JSON.stringify(body).length > ASSISTANT_MAX_REQUEST_BYTES);
    };
    while (fitted.length > 1 && overCaps(fitted)) {
        fitted = fitted.slice(1);
    }
    const newest = fitted[0];
    if (fitted.length === 1 && newest !== undefined && overCaps(fitted)) {
        let only = truncateMessageText(newest, ASSISTANT_MAX_MESSAGE_CHARS);
        // JSON escaping can keep the serialized form over the byte cap even once
        // the raw text fits — shrink further until the serialized bytes fit too.
        // (An approval message is measured against its own byte cap, not this one.)
        let budget = ASSISTANT_MAX_MESSAGE_CHARS;
        while (overCaps([only]) && budget > 1_000) {
            budget = Math.floor(budget * 0.9);
            only = truncateMessageText(newest, budget);
        }
        fitted = [only];
    }
    return fitted;
}
